import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PauseCircle, PlayCircle, Plus, Plug, RefreshCw, TestTube, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useIntegrations,
  useTestIntegration,
  useDeleteIntegration,
  usePatchIntegration,
} from "@/hooks/use-api";
import { SOURCE_TYPE_LABELS } from "@/lib/integration-meta";
import {
  describeTestError,
  filterIntegrations,
  sortIntegrations,
  type RowTestState,
  type SortDir,
  type SortKey,
} from "@/lib/integration-grid";
import { IntegrationGrid } from "@/components/integrations/integration-grid";
import { useAuth } from "@/hooks/use-auth";
import { SyslogSources } from "@/components/integrations/syslog-sources";
import { AddIntegrationDialog } from "@/components/integrations/add-integration-dialog";
import { ConnectMicrosoft } from "@/components/integrations/connect-microsoft";
import type { Integration, IntegrationSourceType } from "@/types/api";

type TypeFilter = Integration["source_type"] | "all";
type StatusFilter = Integration["health_status"] | "paused" | "all";

export default function IntegrationsPage() {
  const { user } = useAuth();
  // Every /api/v1/syslog/sources route is @require_role("admin", "owner"),
  // so showing the card to an analyst yields a 403 dressed up as a
  // transient failure and an Add button that can never succeed.
  const isAdmin =
    user?.roles?.includes("admin") || user?.roles?.includes("owner") || false;
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, isError, refetch } = useIntegrations();
  const testMutation = useTestIntegration();
  const deleteMutation = useDeleteIntegration();
  const patchMutation = usePatchIntegration();

  const integrations = useMemo(() => data?.integrations ?? [], [data]);

  const requestedSource = searchParams.get("add") as IntegrationSourceType | null;
  const [dialogOpen, setDialogOpen] = useState(() => requestedSource !== null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("updated");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [testResults, setTestResults] = useState<Map<string, RowTestState>>(new Map());
  const [testingIds, setTestingIds] = useState<Set<string>>(new Set());
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<string[] | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  useEffect(() => {
    if (requestedSource) setDialogOpen(true);
  }, [requestedSource]);

  function handleDialogOpenChange(open: boolean) {
    setDialogOpen(open);
    if (!open && searchParams.has("add")) {
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
  }

  const visible = useMemo(
    () =>
      sortIntegrations(
        filterIntegrations(
          integrations,
          { type: typeFilter, status: statusFilter, errorsOnly },
          testResults,
        ),
        sortKey,
        sortDir,
      ),
    [integrations, typeFilter, statusFilter, errorsOnly, testResults, sortKey, sortDir],
  );

  function clearSelection() {
    setSelectedIds(new Set());
  }

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelectedIds((prev) => {
      const allSelected = visible.length > 0 && visible.every((i) => prev.has(i.id));
      return allSelected ? new Set() : new Set(visible.map((i) => i.id));
    });
  }

  /** Run a single test; updates per-row state and returns the outcome phase. */
  async function runTest(id: string): Promise<"success" | "warning" | "error"> {
    setTestingIds((prev) => new Set(prev).add(id));
    setTestResults((prev) => {
      const m = new Map(prev);
      m.delete(id);
      return m;
    });
    try {
      const result = await testMutation.mutateAsync(id);
      const state: RowTestState =
        result.status === "warning"
          ? { phase: "warning", result }
          : { phase: "success", result };
      setTestResults((prev) => new Map(prev).set(id, state));
      return result.status;
    } catch (err) {
      setTestResults((prev) =>
        new Map(prev).set(id, { phase: "error", error: describeTestError(err) }),
      );
      return "error";
    } finally {
      setTestingIds((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
    }
  }

  async function handleBulkTest() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setSummary(null);
    const outcomes = await Promise.all(ids.map((id) => runTest(id)));
    const passed = outcomes.filter((o) => o === "success").length;
    const warned = outcomes.filter((o) => o === "warning").length;
    const failed = outcomes.filter((o) => o === "error").length;
    setSummary(
      `Tested ${ids.length}: ${passed} passed, ${warned} warning, ${failed} error`,
    );
  }

  async function confirmDelete() {
    const ids = deleteTarget ?? [];
    setDeleteTarget(null);
    if (ids.length === 0) return;
    const results = await Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id)),
    );
    const okIds = ids.filter((_, i) => results[i].status === "fulfilled");
    const failedIds = ids.filter((_, i) => results[i].status === "rejected");

    // Prune session test state for rows that are gone.
    setTestResults((prev) => {
      const m = new Map(prev);
      okIds.forEach((id) => m.delete(id));
      return m;
    });
    // Keep only the rows that failed to delete selected, so the user can retry.
    setSelectedIds(new Set(failedIds));

    if (failedIds.length > 0) {
      setSummary(
        `Deleted ${okIds.length} of ${ids.length}; ${failedIds.length} failed (still selected)`,
      );
    } else if (ids.length > 1) {
      setSummary(`Deleted ${okIds.length} integrations`);
    } else {
      setSummary(null);
    }
  }

  const selectedIntegrations = integrations.filter((i) => selectedIds.has(i.id));
  const anyEnabled = selectedIntegrations.some((i) => i.user_enabled);
  const allDisabled = selectedIntegrations.length > 0 && selectedIntegrations.every((i) => !i.user_enabled);

  async function handleToggleEnabled(id: string, enable: boolean) {
    setTogglingIds((prev) => new Set(prev).add(id));
    try {
      await patchMutation.mutateAsync({ integrationId: id, enabled: enable });
    } finally {
      setTogglingIds((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
    }
  }

  async function handleBulkToggleEnabled(enable: boolean) {
    // Only send PATCH to rows that need the state change (skip already-matching rows).
    const toChange = selectedIntegrations.filter((i) => i.user_enabled !== enable);
    if (toChange.length === 0) return;
    setSummary(null);
    toChange.forEach((i) => setTogglingIds((prev) => new Set(prev).add(i.id)));
    const results = await Promise.allSettled(
      toChange.map((i) => patchMutation.mutateAsync({ integrationId: i.id, enabled: enable })),
    );
    toChange.forEach((i) =>
      setTogglingIds((prev) => {
        const s = new Set(prev);
        s.delete(i.id);
        return s;
      }),
    );
    const okCount = results.filter((r) => r.status === "fulfilled").length;
    const failCount = results.filter((r) => r.status === "rejected").length;
    if (failCount > 0) {
      setSummary(
        `${enable ? "Enabled" : "Disabled"} ${okCount} of ${toChange.length}; ${failCount} failed`,
      );
    } else if (toChange.length > 1) {
      setSummary(`${enable ? "Enabled" : "Disabled"} ${okCount} integrations`);
    } else {
      setSummary(null);
    }
  }

  const deleteIds = deleteTarget ?? [];
  const deleteItems = deleteIds
    .map((id) => ({ id, name: integrations.find((i) => i.id === id)?.display_name }))
    .filter((x): x is { id: string; name: string } => Boolean(x.name));

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Integrations</h1>
          <p className="text-sm text-muted-foreground">
            Manage data source connections and agent deployments
          </p>
        </div>
        <Button onClick={() => handleDialogOpenChange(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add Integration
        </Button>
      </div>

      <ConnectMicrosoft showAdvancedLink onAdvancedClick={() => handleDialogOpenChange(true)} />

      {isAdmin && <SyslogSources />}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value as TypeFilter);
            clearSelection();
          }}
          aria-label="Filter by type"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Types</option>
          {Object.entries(SOURCE_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as StatusFilter);
            clearSelection();
          }}
          aria-label="Filter by status"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Statuses</option>
          <option value="healthy">Healthy</option>
          <option value="unhealthy">Unhealthy</option>
          <option value="paused">Paused</option>
          <option value="disabled">Disabled</option>
          <option value="unknown">Unknown</option>
        </select>

        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={errorsOnly}
            onChange={(e) => {
              setErrorsOnly(e.target.checked);
              clearSelection();
            }}
            className="h-4 w-4 rounded border-input accent-primary"
          />
          Errors only
        </label>

        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/50 px-4 py-2">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkTest}
              disabled={testingIds.size > 0}
            >
              <TestTube className="mr-1.5 h-3.5 w-3.5" />
              Test selected
            </Button>
            {allDisabled ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkToggleEnabled(true)}
                disabled={patchMutation.isPending}
              >
                <PlayCircle className="mr-1.5 h-3.5 w-3.5" />
                Enable selected
              </Button>
            ) : anyEnabled ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkToggleEnabled(false)}
                disabled={patchMutation.isPending}
              >
                <PauseCircle className="mr-1.5 h-3.5 w-3.5" />
                Disable selected
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteTarget(Array.from(selectedIds))}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Delete selected
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Status / summary messages */}
      {isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Failed to load integrations. Data will retry automatically.
        </div>
      )}
      {summary && (
        <div className="rounded-lg border bg-muted/50 px-4 py-2 text-sm text-muted-foreground">
          {summary}
        </div>
      )}

      {/* Empty state (no integrations at all) */}
      {!isLoading && !isError && integrations.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <div className="rounded-full bg-muted p-3">
            <Plug className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-sm font-semibold">No integrations configured</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Add your first integration to start monitoring.
          </p>
          <Button className="mt-4" variant="outline" onClick={() => setDialogOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add Integration
          </Button>
        </div>
      ) : (
        <IntegrationGrid
          integrations={visible}
          isLoading={isLoading}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onSelectAll={selectAll}
          testResults={testResults}
          testingIds={testingIds}
          onTest={runTest}
          onDelete={(id) => setDeleteTarget([id])}
          onToggleEnabled={handleToggleEnabled}
          togglingIds={togglingIds}
        />
      )}

      {/* Delete confirm modal (single or bulk) */}
      <Dialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Delete {deleteIds.length} integration
              {deleteIds.length === 1 ? "" : "s"}?
            </DialogTitle>
            <DialogDescription>
              This permanently removes the connection
              {deleteIds.length === 1 ? "" : "s"}. Ingested events are retained.
            </DialogDescription>
          </DialogHeader>
          {deleteItems.length > 0 && deleteItems.length <= 5 && (
            <ul className="list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
              {deleteItems.map((item) => (
                <li key={item.id}>{item.name}</li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add dialog */}
      <AddIntegrationDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        initialSourceType={requestedSource}
      />
    </div>
  );
}
